/**
 * @module app.core
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting, IWindowMessage } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { ICorrespondenceConfig } from "app/core/models/essette-interfaces";
import { IPopup } from 'app/shared/elements/popup-base';
import { CorrespondenceHistoryGrid } from 'app/core/components/correspondence/correspondence-history-grid';
import { apiCall } from 'app/shared/lib/api/api';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { MemberService } from 'app/cd/services/member-service';
import { deepClone } from 'app/shared/lib/utilities';
import { CorrespondenceAssociatedProvidersGrid } from 'app/core/components/correspondence/correspondence-associated-providers-grid';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { I18N } from 'aurelia-i18n';
import { ApiQueryResponse } from 'app/shared/lib/essette-api';
import {
    createCorrespondenceCache,
    ICorrespondenceCache
} from 'app/core/caches/correspondence-cache';
import { UseContainerCache } from 'app/shared/lib/cache/use-container-cache';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

const ASSOCIATED_RECIPIENT_ID = '9005';
const ASSOCIATED_TARGET_TYPE_ID = '9004';

@autoinject()
@UseContainerCache({ cacheFactory: createCorrespondenceCache })
export class Correspondence extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    @bindable()
    protected cacheKey: string | null = null;
    declare cache: ICorrespondenceCache;

    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected targetId!: string;
    @bindable.string({ defaultBindingMode: bindingMode.toView })
    protected memberId!: string;
    @bindable.object({ defaultBindingMode: bindingMode.toView })
    protected config!: ICorrespondenceConfig;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = false;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected canRequest: boolean = false;

    protected newLetter: Core.CorrespondenceLetter = {};
    protected letters: ReadonlyArray<Core.CorrespondenceLetter> = [];
    protected letterTemplates: ReadonlyArray<Core.CorrespondenceTemplate> = [];
    protected letterStatuses: ReadonlyArray<Core.CorrespondenceLetterStatus> = [];
    protected selectedProviders: ReadonlyArray<CD.MemberProvider> = [];
    protected selectedTemplate: Core.CorrespondenceTemplate | null = null;
    protected selectedVersion: Core.CorrespondenceTemplateVersion | null = null;
    protected selectedLetter: Core.CorrespondenceLetter | null = null;
    protected selectedLetterStatus: Core.CorrespondenceLetterStatus | null = null;
    protected selectedLetterStatusReason: Core.CorrespondenceLetterStatusReason | null = null;
    protected selectedLetterStatusReasonDetail: string | null = null;
    protected maxLetterStatusReasonDetailLength: number = 500;
    protected memberCultureId: string | null = null;

    protected hasAddCorrespondence: boolean = false;
    protected hasCancelCorrespondence: boolean = false;
    protected isAssociatedProviderTemplate: boolean = false;

    protected pageSize: number = 25;
    protected sorting = new Sorting();
    protected resultCount: number = 0;

    protected validate = validate;

    private listener: (event: any) => {};

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<Core.CorrespondenceLetter>;
    protected $pager!: EssPagerCustomElement;
    protected $associatedProvidersGrid!: CorrespondenceAssociatedProvidersGrid;
    protected $historyGrid!: CorrespondenceHistoryGrid;
    protected $cancelModal!: IPopup;

    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly service: CorrespondenceService,
        private readonly memberService: MemberService,
        protected readonly transitional: TransitionalManager,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);

        this.listener = this.handleTransitionalMessage.bind(this);
    }

    protected bind(): void {
        if(!this.targetId) { throw new Error('Bindable property [target-id] was not set'); }
        if(!this.memberId) { throw new Error('Bindable property [member-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        const targetChanged = this.cache.targetId !== this.targetId;
        const memberChanged = this.cache.memberId !== this.memberId;
        const moduleChanged = this.cache.moduleCode !== this.config.moduleCode;
        this.cache.refreshChildren = memberChanged || moduleChanged || targetChanged;
        this.cache.targetId = this.targetId;
        this.cache.memberId = this.memberId;
        this.cache.moduleCode = this.config.moduleCode;

        this.service.configure(this.config);  // Configures the letter service to use correct module routes

        this.checkPermissions();

        // member culture id
        if(!this.cache.memberCultureId || memberChanged) {
            this.loadMemberCulture();
        } else {
            this.memberCultureId = this.cache.memberCultureId;
        }

        // letter templates
        if(!this.cache.isFresh('letterTemplates') || moduleChanged) {
            this.loadTemplates();
        } else {
            this.letterTemplates = this.cache.letterTemplates;
        }

        // letter statuses
        if(!this.cache.isFresh('letterStatuses')) {
            this.loadStatuses();
        } else {
            this.letterStatuses = this.cache.letterStatuses;
        }

        // letters
        if(!this.cache.isFresh('letters') || memberChanged || moduleChanged || targetChanged) {
            this.loadPendingLetters();
        } else {
            this.loadFromCache();
        }
    }

    protected attached(): void {
        if(this.$table && this.sorting.entries.length > 0) {
            this.$table.applySorting(this.sorting);
        }
    }

    protected detached(): void {
        window.removeEventListener('message', this.listener, false);
    }

    ////****************** Events ***********************************************////
    protected selectedTemplateChanged(): void {
        this.isAssociatedProviderTemplate = false;

        if(!this.selectedTemplate) {
            // deselect version if no selected template
            this.selectedVersion = null;
            return;
        };

        // load template copies
        if(this.selectedTemplate.id) {
            this.loadTemplateCopies(this.selectedTemplate.id).then((copies) => {
                const templateCopies: Core.CorrespondenceTemplateCopy[] = copies;

                // check if template copy recipients are associated providers
                if(templateCopies.map(copy => copy.recipient?.id).indexOf(ASSOCIATED_RECIPIENT_ID) > -1) {
                    // Load Associated Providers Grid

                    this.isAssociatedProviderTemplate = true;
                }

            });
        }

        // get the filtered list of versions
        const versions = (this.selectedTemplate.versions || []).filter((v) => {
            return this.languageFilterFunction(v);
        });

        if(versions) {
            this.selectedTemplate.versions = versions.sort((a, b) => { return a.language!.sortOrder! - b.language!.sortOrder! });

            if(this.selectedVersion) {
                // language is already selected, select only if available.
                this.selectedVersion = versions.find(v => v.language?.id === this.selectedVersion?.language?.id) || null;
            } else if(this.cache.memberCultureId) {
                // default to member's language, if available
                this.selectedVersion = versions.find(v => v.language?.applicationCulture?.id === this.cache.memberCultureId) || null;
            }
        }
    }

    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCancelCorrespondence = this.accountContext.hasPermission('Core.CorrespondenceLetterCopy.UpdateStatus');
        if(this.config?.targetType === 'CM.Case') {
            this.hasAddCorrespondence = this.accountContext.hasPermission('CM.CaseCorrespondenceLetter.Insert');
        }
        else if(this.config?.targetType === 'UM.Auth') {
            this.hasAddCorrespondence = this.accountContext.hasPermission('UM.AuthCorrespondenceLetter.Insert');
        }
    }

    protected templateFilterFunction(template: Core.CorrespondenceTemplate) {
        return template.versions && template.versions.length > 0;
    }

    protected languageFilterFunction(version: Core.CorrespondenceTemplateVersion) {
        return version.flag && version.flag.isCurrent === true;
    }

    protected statusFilterFunction(status: Core.CorrespondenceLetterStatus) {
        return status.code === 'CA.Canceled';
    }

    public async reload(): Promise<void> {
        this.isAssociatedProviderTemplate = false;

        await this.loadPendingLetters();
    }

    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            const sortDirection = direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;
            if(sorting.entries.findIndex(entry => entry.reference === property && entry.direction === sortDirection) > -1) return;
            sorting.add({
                reference: property,
                direction: sortDirection
            });
        }
        await this.loadPendingLetters();
    }

    protected openLetter(letterId: string): void {
        const w: Window | null = open('', 'Popup_Correspondence', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        if(w && this.config) {
            this.transitional.openTransitional(w, this.config.transitionalPageId, [letterId], this.config.moduleCode);

            window.addEventListener('message', this.listener, false);
        }
    }

    protected openCancelLetterPanel(letter: Core.CorrespondenceLetter): void {
        if(!this.cache.letterStatuses.length) this.loadStatuses();
        this.selectedLetter = letter;
        this.$cancelModal.open();
    }

    protected clearCancelLetterPanel() {
        this.selectedLetterStatus = null;
        this.selectedLetterStatusReason = null;
        this.selectedLetterStatusReasonDetail = null;
        this.$cancelModal.close();
    }

    private async handleTransitionalMessage(event: MessageEvent): Promise<void> {
        if(!this.transitional.checkOrigin(event.origin)) {
            this.logger.error(`Transitional message event origin was not allowed: ${event.origin}`);
            return;
        }

        const message: IWindowMessage = event.data ?? null;
        if(!message) {
            this.logger.error(`Transitional message event did not contain data`);
            return;
        }

        if(message.action === 'REFRESH') {
            await this.reload();
            if(this.$historyGrid) {
                await this.$historyGrid.reload();
            }
        }
    }

    protected loadFromCache(): void {
        if(!!this.cache.letters) this.letters = this.cache.letters;
        if(!!this.cache.lettersPage) this.$pager.page = this.cache.lettersPage;
        if(!!this.cache.lettersSorting) this.sorting = this.cache.lettersSorting;
        if(!!this.cache.lettersResultCount) this.resultCount = this.cache.lettersResultCount;
    }

    protected setCache(): void {
        this.cache.letters = this.letters;
        this.cache.lettersPage = this.$pager.page;
        this.cache.lettersSorting = this.sorting;
        this.cache.lettersResultCount = this.resultCount;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async insertLetter(): Promise<void> {

        //TODO: validation!!!

        const newLetter: Core.CorrespondenceLetter = {
            template: {
                id: this.selectedTemplate && this.selectedTemplate.id,
                version: {
                    language: {
                        code: this.selectedVersion && this.selectedVersion.language && this.selectedVersion.language.code || null,
                        applicationCulture: {
                            code: this.selectedVersion && this.selectedVersion.language &&
                                this.selectedVersion.language.applicationCulture &&
                                this.selectedVersion.language.applicationCulture.code || null
                        }
                    }
                }
            }
        };

        if(this.config?.addTargetProperty) {
            this.config.addTargetProperty(newLetter);
        }

        // To set for the toaster display
        let lettersAddedSuccessfully = true;
        const numberOfLetters = this.selectedProviders.length;

        try {
            this.state.beginSave();
            // Saving takes a while if multiple providers are selected, so display loading bar gradient via load state
            this.state.beginLoad();

            const promises = new Array<Promise<IApiActionResults>>();
            if(this.isAssociatedProviderTemplate) {
                this.selectedProviders.forEach(p => {
                    const letter = deepClone(newLetter);
                    letter.recipient = {
                        id: p.provider?.location?.id,
                        type: {
                            id: ASSOCIATED_TARGET_TYPE_ID
                        }
                    };

                    letter.description = p.provider?.name?.full + " (" + p.relationship?.description + ")";
                    promises.push(this.service.insertLetter(letter));
                });
            }
            else {
                promises.push(this.service.insertLetter(newLetter));
            }

            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);

            // When all finished, generate the snippets per successfully generated letter
            for(const r of responses) {
                if(!r.isSuccessful) {
                    lettersAddedSuccessfully = false;
                    continue;
                };

                const response = await this.transitional.post(`?action=generateLetter&LetterID=${r.id}&ModuleCode=${this.config.moduleCode}`);
                lettersAddedSuccessfully = lettersAddedSuccessfully && response.isSuccessful;
            }

            this.selectedTemplate = null;
            this.selectedVersion = null;
            await this.reload();
            if(this.$historyGrid) {
                await this.$historyGrid.reload();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.created('core:correspondenceLetter.letterWithSnippets.$', lettersAddedSuccessfully, numberOfLetters);
            this.state.endSave();
            this.state.endLoad();
        }
    }

    @apiCall
    protected async cancelLetter(): Promise<void> {

        //TODO: validation!!!

        const canceledLetter: Core.CorrespondenceLetter = {
            id: this.selectedLetter && this.selectedLetter.id || null,

            status: {
                id: this.selectedLetterStatus?.id || null,
                reason: {
                    id: this.selectedLetterStatusReason?.id || null
                }
            }
        };

        if(this.selectedLetterStatusReason?.flag?.requireStatusDetails && canceledLetter.status) {
            canceledLetter.status.details = this.selectedLetterStatusReasonDetail;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.cancelLetter(canceledLetter);
            isSuccessful = response.isSuccessful;
            this.selectedLetter = null;
            this.clearCancelLetterPanel();
            await this.reload();
            if(this.$historyGrid) {
                await this.$historyGrid.reload();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            if(isSuccessful) this.toaster.info().message(this.i18n.tr('core:local.correspondence.letterCanceled')).show();
            else this.toaster.error().message(this.i18n.tr('core:local.correspondence.letterNotCanceled')).show();
            this.state.endSave();
        }
    }

    @apiCall
    private async loadMemberCulture(): Promise<void> {
        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMember(this.memberId, ['language.reads.culture'], ['language.reads.culture.id']);
            if(response.results[0].language?.reads?.culture?.id) {
                this.memberCultureId = this.cache.memberCultureId = response.results[0].language.reads.culture.id;
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadTemplates(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'versions',
            'versions.language'
        ];
        const fields = [
            'description',
            'versions.flag.isCurrent',
            'versions.language.applicationCulture.id',
            'versions.language.code',
            'versions.language.description',
            'versions.language.sortOrder'
        ];

        try {
            this.state.beginLoad();
            const templates = await this.service.loadTemplates(options, includes, fields);

            this.letterTemplates = this.cache.letterTemplates = templates.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadStatuses(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = ['reasons'];
        const fields = [
            'code',
            'description',
            'reasons.description',
            'reasons.flag.requireStatusDetails'
        ];

        try {
            this.state.beginLoad();
            const statuses = await this.service.loadStatuses(options, includes, fields);

            this.letterStatuses = this.cache.letterStatuses = statuses.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadTemplateCopies(templateId: string): Promise<Core.CorrespondenceTemplateCopy[]> {
        const options: Partial<IQueryLoadOptions> = {};

        const includes = ['recipient'];
        const fields = ['recipient.id'];

        try {
            this.state.beginLoad();

            const response = await this.service.loadTemplateCopiesByTemplateId(templateId, options, includes, fields);

            return response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadPendingLetters(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0
        };

        const includes = [
            'template',
            'template.version',
            'template.version.language',
            'metadata.created.byUser',
            'status',
            'copies',
            'copies.deliveryMethod',
            'copies.delivered.recipient',
            'copies.status',
            'copies.coverSheet.templateVersion.template',
            'attachedTo',
            'attachedTo.type'
        ];

        const fields = [
            'description',
            'metadata.created.byUser.description',
            'metadata.created.timestamp',
            'status.description',
            'template.description',
            'template.version.language.description',
            'attachedTo.id',
            'attachedTo.type.code'
        ];

        if(this.config.moduleCode === 'UM') {
            includes.push('auth');
            fields.push('auth.number');
        }

        try {
            this.state.beginLoad();

            let response: ApiQueryResponse<Core.CorrespondenceLetter>;
            if(this.config.rollupByMember) {
                response = await this.service.loadLettersByMember(this.memberId, false, options, includes, fields);
            } else {
                response = await this.service.loadLettersByTarget(this.targetId, false, options, includes, undefined, fields);
            }

            this.letters = response.results;
            this.resultCount = response.count || 0;
            this.setCache();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
